import classNames from 'classnames';
import React from 'react';
import { Fund } from 'src/components/Exchange/types';
import { IRA_AUTOMAX_START_DATE_TOOL_TIP } from 'src/redux/modules/modals/modals.module';
import { selectableDays, selectableMonths } from 'src/settings/settings';
import { SelectableGrid } from '../../Shared/SelectableGrid';
import Heading from '../../Shared/Heading/Heading';
import './IRAAutoMaxEditor.scss';
import { useSelector } from 'react-redux';
import { getIRALimitData } from 'src/redux/modules/funds/IRA/IRA.selectors';
import IRAContributionsAvailableTip from '../../Buy/BuyFlow/IRAContributionsEditor/IRAContributionsAvailableTip';
import {
  getContributionPerAccount,
  getIRAAutoMaxCartDetailsRaw,
  contributionMinimum,
  getContributionPerAccountMeetsMinimum,
  getActualCurrentYearRemainingContributions,
} from 'src/redux/modules/funds/IRAAutoMaxCartDetails/IRAAutoMaxCartDetails.selectors';
import numeral from 'numeral';
import ToolTip from 'src/components/Shared/ToolTip/ToolTip';
import { showStartDateToolTip } from '../../../utils/automatic/automatic-utils.js';

const fmtCurrency = (amt) => numeral(amt).format('$0,0.00');

export interface IRAAutoMaxEditorProps {
  customerWebViewEnabled: boolean;
  isSalesforceRep: boolean;
  fund: Fund;
  actions: Record<string, CallableFunction>;
  minValue: number;
  maxValue: number;
}
const IRAAutoMaxEditor = ({ actions }) => {
  const iraLimitData = useSelector(getIRALimitData);
  const actualCurrentYearRemainingContributions = useSelector(
    getActualCurrentYearRemainingContributions
  );
  const iRAAutoMaxCartDetails = useSelector(getIRAAutoMaxCartDetailsRaw);
  const contributionPerAccount = useSelector(getContributionPerAccount);
  const meetsMinimum = useSelector(getContributionPerAccountMeetsMinimum);

  const { currentYearContributionsSoFar, currentYearMaximumLimit } =
    iraLimitData;

  const { openModal } = actions;

  return (
    <React.Fragment>
      <div className="ira-automax-editor-wrapper">
        <div className={'ira-automax-editor-container'}>
          <div className={'ira-automax-editor-title'}>
            <Heading contextStyle={{ fontWeight: 'bold' }} headingType="h3">
              Set contribution schedule for selected fund(s)
            </Heading>
          </div>
          <div className="ira-automax-editor-contribution-summary">
            <Heading headingType="h4" styleName="ira-automax-editor-subhead">
              Current Contribution Summary
              <ToolTip message={<IRAContributionsAvailableTip />} />
            </Heading>
            <div className="ira-automax-editor-top-row">
              <div className="ira-automax-editor-top-col">
                <div className="ira-automax-editor-cell-label">
                  Remaining Allowed
                </div>
                <div className="ira-automax-editor-cell-value strong">
                  {fmtCurrency(actualCurrentYearRemainingContributions)}
                </div>
              </div>
              <div className="ira-automax-editor-top-col">
                <div className="ira-automax-editor-cell-label">
                  Year-to-Date Contributions
                </div>
                <div className="ira-automax-editor-cell-value">
                  {fmtCurrency(currentYearContributionsSoFar)}
                </div>
              </div>
              <div className="ira-automax-editor-top-col">
                <div className="ira-automax-editor-cell-label">
                  Maximum Allowed
                </div>
                <div className="ira-automax-editor-cell-value">
                  {fmtCurrency(currentYearMaximumLimit)}
                </div>
              </div>
            </div>
          </div>

          <div className={classNames('ira-automax-editor-input-container')}>
            <div className="ira-automax-editor-recurring-amount">
              <br />
              <Heading headingType="h4" styleName="ira-automax-editor-subhead">
                Contribution Per Account
              </Heading>

              <div
                className={classNames(
                  'ira-automax-editor-contribution-info-container',
                  {
                    'under-min': contributionPerAccount > 0 && !meetsMinimum,
                  }
                )}
              >
                <span className="per-account">
                  {contributionPerAccount
                    ? fmtCurrency(contributionPerAccount)
                    : '$--'}
                  *
                </span>
                <div className="min">
                  {contributionPerAccount > 0 && !meetsMinimum && (
                    <span>
                      Min {fmtCurrency(contributionMinimum)}
                      <br />
                    </span>
                  )}
                </div>
                <div className="legal">
                  *The amount is estimated based on the remaining eligible
                  current year dates and remaining available contribution limit.
                </div>
              </div>
            </div>
            <div className="ira-automax-editor-frequency-container">
              <SelectableGrid
                showSelectAllOption
                allItems={selectableMonths}
                buttonProps={{
                  btnSize: 64,
                  phoneColNum: 3,
                  idPrefix(id) {
                    return `monthButton_${id}`;
                  },
                }}
                handleSelect={actions.setIRAAutoMaxMonths}
                id={`month-chart-ira-automax`}
                optionName="Months"
                selectedItems={iRAAutoMaxCartDetails.monthsForPayments}
                title="Months"
              />
            </div>
            <div className="ira-automax-editor-date-container">
              <SelectableGrid
                allItems={selectableDays}
                buttonProps={{
                  btnSize: 40,
                  phoneColNum: 5,
                  idPrefix(id) {
                    return `dayButton_${id}`;
                  },
                }}
                handleSelect={actions.setIRAAutoMaxDays}
                id={`day-chart-ira-automax`}
                limitText="Max: 4 days per month"
                maxSelectCount={4}
                selectedItems={iRAAutoMaxCartDetails.daysOfMonthPayments}
                showSelectAllOption={false}
                title="Date(s)"
              >
                <div className="ira-automax-editor-disclaimer-txt">
                  <span>
                    <a
                      className="ira-automax-editor-link college-branded-link"
                      onClick={() =>
                        showStartDateToolTip(
                          openModal,
                          IRA_AUTOMAX_START_DATE_TOOL_TIP
                        )
                      }
                    >
                      View important information
                    </a>{' '}
                    regarding scheduled dates of your automatic transactions.
                  </span>
                </div>
              </SelectableGrid>
            </div>
          </div>

          <div className="ira-automax-editor-legal-note-container">
            <div className="ira-automax-editor-legal-note">
              <strong className="title">
                You will be auto-enrolled for next year and beyond.
              </strong>
              Your IRA AutoMax will continue on the same schedule next year and
              beyond unless cancelled. Each transaction amount may change based
              on your schedule, remaining allowed or your IRA contribution limit
              increases.
            </div>
            <div className="ira-automax-editor-legal-note">
              <strong className="title">
                Exisiting systematic IRA purchases
              </strong>
              Your IRA AutoMax must be the only recurring purchase on your IRAs.
              When you enroll in IRA AutoMax, we will remove all other automatic
              IRA contribution services, such as Automatic Buys.
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default IRAAutoMaxEditor;
